import type { User } from "oidc-client-ts";

import {
  XEMPLO_AUTH_ACTIVE_SUB_KEY,
  XEMPLO_AUTH_LOGOUT_KEY,
} from "@xemplo/cross-app-events";
import { safeCall } from "@xemplo/safe-call";

import env from "./environment";

// localStorage (not sessionStorage) so the value survives opening the app in
// a new tab; cleared on logout via the app-settings `logoutCallback`
// (src/index.tsx) so a later sign-in doesn't inherit a stale session id.
export const DS_SESSION_ID_STORAGE_KEY = "dsSessionId";

/**
 * Runs once the `/confirm-login` code exchange succeeds, before the user lands
 * in auth state.
 *
 * `useCrossAppAuthListener` (App.tsx) drops the user and routes to /login when
 * the cross-app logout marker is newer than the token's `auth_time`, or the
 * active-sub marker names another identity. `@xemplo/auth-helper`'s `useLogin`
 * resets both markers after sign-in, but GP has its own Login page that never
 * did — looping /login → IdP → /confirm-login until the user cleared site data.
 *
 * It has to happen here rather than in Login.tsx: the listener is mounted
 * ahead of the routes, so its effect would read the stale marker first.
 */
const onSigninCallback = (user: User | void) => {
  safeCall(() => localStorage.removeItem(XEMPLO_AUTH_LOGOUT_KEY));
  const sub = user?.profile?.sub;
  if (sub) {
    safeCall(() => localStorage.setItem(XEMPLO_AUTH_ACTIVE_SUB_KEY, sub));
  }

  // Passing onSigninCallback replaces the provider's default, which is what
  // strips ?code&state. Left in the URL, a refresh would replay the spent code
  // and fail the callback.
  window.history.replaceState({}, document.title, window.location.pathname);
};

export const AUTH_CONFIG = {
  onSigninCallback,
  authority: env.identity_baseurl,
  client_id: "xemplo.frontend",
  redirect_uri: env.callback_url,
  post_logout_redirect_uri: env.redirect_url,
  silent_redirect_uri: undefined,
  response_type: "code",
  scope: "openid profile email address standard_api IdentityServerApi xemplo offline_access",
  automaticSilentRenew: false,
  includeIdTokenInSilentRenew: undefined,
  accessTokenExpiringNotificationTimeInSeconds: 120,
  // Polls the IdP's check_session_iframe so a logout in another app on the
  // same IdP (e.g. HR) raises `userSignedOut` here too — required for
  // useCrossAppAuthListener to detect cross-origin logout.
  monitorSession: true,
  revokeTokensOnSignout: true
};
